import { createApp } from './index';
import { ROUTER_SKIP_FETCH_DATA_PROPERTY } from 'frontend/router';

//
// See https://ssr.vuejs.org/guide/data.html#client-data-fetching for explanations.
//
const { app, router, store } = createApp(false);

//
// When using template, context.state will automatically be embedded in the final HTML as
// window.__INITIAL_STATE__ state. On the client, the store should pick up the state before
// mounting the application.
//
if ((window as any).__INITIAL_STATE__) {
  store.replaceState((window as any).__INITIAL_STATE__);
}

//
// ## Resolve data before route navigation:
//
// With this strategy, the app will stay on the current view until the data needed by the
// incoming view has been resolved. The benefit is that the incoming view can directly render
// the full content when it's ready, but if the data fetching takes a long time, the user will
// feel "stuck" on the current view. It is therefore recommended to provide a data loading
// indicator if using this strategy.
//
// We can implement this strategy on the client by checking matched components and invoking
// their asyncData function inside a global route hook. Note we should register this hook
// after the initial route is ready so that we don't unnecessarily fetch the server-fetched
// data again.
//
router.onReady(() => {
  // Add router hook for handling asyncData. Doing it after initial route is resolved so that
  // we don't double-fetch the data that we already have. Using `router.beforeResolve()` so
  // that all async components are resolved.

  // fetch on the first request if SSR isn't on
  if (!(window as any).__INITIAL_STATE__) {
    store.dispatch('fetchResults', router.currentRoute);
  }

  router.beforeResolve((to, from, next) => {
    const skipFetch = (router as any)[ROUTER_SKIP_FETCH_DATA_PROPERTY] === true;
    delete (router as any)[ROUTER_SKIP_FETCH_DATA_PROPERTY];

    if (to.path !== from.path && !skipFetch) {
      const callback = () => next();
      // fetch every time url changes
      store.dispatch('fetchResults', to).then(callback).catch(callback);
    } else {
      next();
    }
  });

  router.onReady(() => app.$mount('#app'));
});
